// src/edu-world/core/event-bus.ts
// Simple typed EventBus for EduWorld (Phase 0)
// Minimal pub/sub — no persistence, no replay.

import { EduWorldEventMap } from '../types/events';

export type EventHandler<T> = (payload: T) => void;

export interface IEventBus {
  emit<K extends keyof EduWorldEventMap>(event: K, payload: Omit<EduWorldEventMap[K], 'timestamp'>): void;
  on<K extends keyof EduWorldEventMap>(event: K, handler: EventHandler<EduWorldEventMap[K]>): void;
  off<K extends keyof EduWorldEventMap>(event: K, handler: EventHandler<EduWorldEventMap[K]>): void;
}

export class EventBus implements IEventBus {
  private listeners: Map<string, Set<EventHandler<unknown>>> = new Map();

  emit<K extends keyof EduWorldEventMap>(
    event: K,
    payload: Omit<EduWorldEventMap[K], 'timestamp'>
  ): void {
    const handlers = this.listeners.get(event as string);
    if (!handlers || handlers.size === 0) return;

    // Inject timestamp automatically
    const fullPayload = { ...payload, timestamp: Date.now() } as EduWorldEventMap[K];

    for (const handler of handlers) {
      try {
        (handler as EventHandler<EduWorldEventMap[K]>)(fullPayload);
      } catch (err) {
        console.error(`[EventBus] Error in handler for "${String(event)}":`, err);
      }
    }
  }

  on<K extends keyof EduWorldEventMap>(
    event: K,
    handler: EventHandler<EduWorldEventMap[K]>
  ): void {
    const key = event as string;
    if (!this.listeners.has(key)) {
      this.listeners.set(key, new Set());
    }
    this.listeners.get(key)!.add(handler as EventHandler<unknown>);
  }

  off<K extends keyof EduWorldEventMap>(
    event: K,
    handler: EventHandler<EduWorldEventMap[K]>
  ): void {
    const handlers = this.listeners.get(event as string);
    if (handlers) {
      handlers.delete(handler as EventHandler<unknown>);
    }
  }

  /** Remove all listeners (useful for cleanup) */
  clear(): void {
    this.listeners.clear();
  }
}
